const API_BASE =
  import.meta.env.VITE_API_URL || 'https://cc-api.viconsignment.ca/api/v1';

/** Same key as storefront so a shared-domain future can share sessions. */
export const TOKEN_KEY = 'cc_customer_token';

export const RESET_PASSWORD_URL = 'https://app.viconsignment.ca/account/reset-password';

export interface Customer {
  id: number;
  email: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  shipping_address?: {
    street?: string;
    city?: string;
    province?: string;
    postal_code?: string;
    country?: string;
  };
  email_verified?: boolean;
}

export interface LoginResult {
  success: boolean;
  customer?: Customer;
  token?: string;
  error?: string;
  needsVerification?: boolean;
}

export interface RegisterResult {
  success: boolean;
  error?: string;
}

function normalizeCustomer(raw: Record<string, unknown>): Customer {
  const c = { ...raw } as unknown as Customer;
  if (typeof c.shipping_address === 'string') {
    try {
      c.shipping_address = JSON.parse(c.shipping_address);
    } catch {
      c.shipping_address = undefined;
    }
  }
  return c;
}

export function getStoredToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

export function saveToken(token: string | null): void {
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch {
    // ignore
  }
}

export async function fetchProfile(token: string): Promise<Customer | null> {
  const res = await fetch(`${API_BASE}/customers/profile`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) return null;
  const json = await res.json();
  const data = json.data ?? json;
  return data?.id ? normalizeCustomer(data) : null;
}

export async function loginCustomer(email: string, password: string): Promise<LoginResult> {
  try {
    const res = await fetch(`${API_BASE}/customers/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const json = await res.json();
    if (!res.ok) {
      return {
        success: false,
        error: json.message || 'Login failed',
        needsVerification: json.needsVerification === true,
      };
    }
    const payload = json.data ?? json;
    const customer = payload.customer ? normalizeCustomer(payload.customer) : undefined;
    const token = payload.token as string | undefined;
    if (token) saveToken(token);
    return { success: true, customer, token };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

export async function registerCustomer(input: {
  email: string;
  password: string;
  firstName?: string;
  lastName?: string;
}): Promise<RegisterResult> {
  try {
    const res = await fetch(`${API_BASE}/customers/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(input),
    });
    const json = await res.json();
    if (!res.ok) {
      return { success: false, error: json.message || 'Registration failed' };
    }
    return { success: true };
  } catch (err) {
    return { success: false, error: err instanceof Error ? err.message : 'Network error' };
  }
}

export async function resendVerification(email: string): Promise<void> {
  await fetch(`${API_BASE}/customers/resend-verification`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email }),
  });
}

export function customerDisplayName(customer: Customer): string {
  const name = [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim();
  return name || customer.email;
}

export function customerContactFromProfile(customer: Customer): {
  name: string;
  email: string;
  phone: string;
} {
  return {
    name: [customer.first_name, customer.last_name].filter(Boolean).join(' ').trim(),
    email: customer.email || '',
    phone: customer.phone || '',
  };
}
